import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { getUserStorage, setUserStorage, migrateToUserStorage } from '../../utils/userStorage';
import Icon from './Icon';
import CloseButton from './CloseButton';

// Keys that need user-specific storage
const SMART_PROMPT_STORAGE_KEYS = ['dismissedSmartPrompts', 'userBehaviorStats'];

/**
 * Context for smart prompts - defined at top to avoid hoisting issues
 */
const SmartPromptsContext = React.createContext({
  stats: {},
  dismissedPrompts: new Set(),
  handleDismiss: () => {}
});

/**
 * Smart prompt definitions based on user behavior patterns.
 * Non-translatable data only (condition, icon, actionPath, priority) — the
 * title/message/actionLabel text lives in src/locales/en/onboarding.json
 * under smartPrompts.<key>. `hasActionLabel` flags whether that key has an
 * actionLabel entry, since the label text itself no longer lives here.
 */
const SMART_PROMPTS = {
  // User has searched but never saved
  searchWithoutSave: {
    condition: (milestones, stats) =>
      milestones.firstSearch && !milestones.firstSave && stats.searchCount >= 3,
    icon: 'lightbulb',
    action: null,
    priority: 1
  },

  // User has saved items but no collection
  savesWithoutCollection: {
    condition: (milestones, stats) =>
      milestones.firstSave && !milestones.firstCollectionCreated && stats.saveCount >= 5,
    icon: 'folder',
    hasActionLabel: true,
    actionPath: '/my-saved-items',
    priority: 2
  },

  // User has saved items but no trip
  savesWithoutTrip: {
    condition: (milestones, stats) =>
      milestones.firstSave && !milestones.firstTripCreated && stats.saveCount >= 3,
    icon: 'airplane',
    hasActionLabel: true,
    actionPath: '/trip/new',
    priority: 3
  },

  // User created trip but hasn't added activities
  tripWithoutActivities: {
    condition: (milestones, stats) =>
      milestones.firstTripCreated && !milestones.firstActivityScheduled && stats.tripCount >= 1,
    icon: 'calendar',
    hasActionLabel: true,
    actionPath: '/activity-planner',
    priority: 2
  },

  // User hasn't explored social feed
  notExploredSocial: {
    condition: (milestones, stats) =>
      milestones.firstTripCreated && !stats.visitedSocial && stats.sessionCount >= 3,
    icon: 'sparkles',
    hasActionLabel: true,
    actionPath: '/social-feed',
    priority: 4
  },

  // User has trip but hasn't shared
  tripNotShared: {
    condition: (milestones, stats) =>
      milestones.firstTripCreated && !milestones.firstTripShared && stats.tripCount >= 1,
    icon: 'share',
    priority: 5
  }
};

/**
 * Get applicable prompts based on user state
 */
function getApplicablePrompts(milestones, stats, dismissedPrompts) {
  return Object.entries(SMART_PROMPTS)
    .filter(([key, prompt]) =>
      !dismissedPrompts.has(key) &&
      prompt.condition(milestones, stats)
    )
    .sort((a, b) => a[1].priority - b[1].priority)
    .map(([key, prompt]) => ({ key, ...prompt }));
}

/**
 * Smart Prompt Card Component
 */
function SmartPromptCard({ prompt, onDismiss, onAction }) {
  const { t } = useTranslation('onboarding');
  return (
    <div className="bg-primary-lighter rounded-card p-4 border border-primary-light">
      <div className="flex items-start gap-3">
        <Icon name={prompt.icon} className="w-6 h-6 text-primary" />
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-content-primary text-sm">{t(`smartPrompts.${prompt.key}.title`)}</h4>
          <p className="text-xs text-content-secondary mt-0.5">{t(`smartPrompts.${prompt.key}.message`)}</p>

          {prompt.hasActionLabel && (
            <button
              onClick={() => onAction(prompt)}
              className="mt-2 inline-flex items-center gap-0.5 text-xs font-medium text-primary hover:text-primary-dark transition-colors duration-fast"
            >
              {t(`smartPrompts.${prompt.key}.actionLabel`)}
              <Icon name="chevronRight" className="w-3.5 h-3.5 flex-shrink-0" />
            </button>
          )}
        </div>
        <CloseButton
          variant="plain"
          size="sm"
          onClick={() => onDismiss(prompt.key)}
          title={t('smartPrompts.dismissTitle')}
          className="-my-2 -mr-2"
        />
      </div>
    </div>
  );
}

/**
 * Smart Prompts Provider
 *
 * Manages smart prompts that appear based on user behavior patterns.
 * Shows prompts in a non-intrusive way to guide users toward features.
 */
export function SmartPromptsProvider({ children }) {
  const { milestones } = useOnboarding();
  const [dismissedPrompts, setDismissedPrompts] = useState(new Set());
  const [stats, setStats] = useState({
    searchCount: 0,
    saveCount: 0,
    tripCount: 0,
    sessionCount: 1,
    visitedSocial: false
  });

  // Load dismissed prompts and stats from user-specific localStorage
  useEffect(() => {
    // Migrate any old non-user-specific keys to user-specific keys
    migrateToUserStorage(SMART_PROMPT_STORAGE_KEYS);

    try {
      const dismissed = getUserStorage('dismissedSmartPrompts', []);
      if (dismissed && dismissed.length > 0) {
        setDismissedPrompts(new Set(dismissed));
      }
    } catch (e) {
      console.error('Error parsing dismissedSmartPrompts:', e);
    }

    try {
      let currentStats = getUserStorage('userBehaviorStats', {
        searchCount: 0,
        saveCount: 0,
        tripCount: 0,
        sessionCount: 0,
        visitedSocial: false
      });

      // Increment session count
      const updatedStats = { ...currentStats, sessionCount: (currentStats.sessionCount || 0) + 1 };
      setStats(updatedStats);
      setUserStorage('userBehaviorStats', updatedStats);
    } catch (e) {
      console.error('Error parsing userBehaviorStats:', e);
    }
  }, []);

  // Sync stats with milestones for cross-device consistency
  useEffect(() => {
    setStats(prev => {
      const updated = { ...prev };
      // Use milestones as source of truth for these flags
      if (milestones.visitedDiscover) updated.visitedSocial = true;
      if (milestones.firstSearch) updated.searchCount = Math.max(prev.searchCount, 1);
      if (milestones.firstSave) updated.saveCount = Math.max(prev.saveCount, 1);
      if (milestones.firstTripCreated) updated.tripCount = Math.max(prev.tripCount, 1);
      return updated;
    });
  }, [milestones]);

  const handleDismiss = useCallback((promptKey) => {
    setDismissedPrompts(prev => {
      const next = new Set(prev);
      next.add(promptKey);
      setUserStorage('dismissedSmartPrompts', [...next]);
      return next;
    });
  }, []);

  return (
    <SmartPromptsContext.Provider value={{ stats, dismissedPrompts, handleDismiss }}>
      {children}
    </SmartPromptsContext.Provider>
  );
}

/**
 * Smart Prompt Display Component
 *
 * Place this component where you want prompts to appear (e.g., homepage, sidebar)
 */
export function SmartPromptDisplay({ maxPrompts = 1, className = '' }) {
  const navigate = useNavigate();
  const { milestones } = useOnboarding();
  const { stats, dismissedPrompts, handleDismiss } = React.useContext(SmartPromptsContext);
  const [prompts, setPrompts] = useState([]);

  useEffect(() => {
    const applicable = getApplicablePrompts(milestones, stats, dismissedPrompts);
    setPrompts(applicable.slice(0, maxPrompts));
  }, [milestones, stats, dismissedPrompts, maxPrompts]);

  const handleAction = useCallback((prompt) => {
    if (prompt.actionPath) {
      navigate(prompt.actionPath);
    }
    handleDismiss(prompt.key);
  }, [handleDismiss, navigate]);

  if (prompts.length === 0) return null;

  return (
    <div className={`space-y-3 ${className}`}>
      {prompts.map(prompt => (
        <SmartPromptCard
          key={prompt.key}
          prompt={prompt}
          onDismiss={handleDismiss}
          onAction={handleAction}
        />
      ))}
    </div>
  );
}

export default SmartPromptsProvider;
